import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import CoverImageUpload from '../../../components/ui/CoverImageUpload';
import { getCdnUrl } from '../../../config/cdn';
import { placeService, City, State } from '../../../services/api';
import { routesService, RouteInput, RouteShopInput } from '../../../services/routes';

interface AdminPlaceOption {
  id: string;
  name: string;
  city: string;
  state: string;
}

interface ScopeOption {
  value: string;
  label: string;
}

const regionOptions: ScopeOption[] = [
  { value: 'norte', label: 'Norte' },
  { value: 'nordeste', label: 'Nordeste' },
  { value: 'centro-oeste', label: 'Centro-Oeste' },
  { value: 'sudeste', label: 'Sudeste' },
  { value: 'sul', label: 'Sul' },
];

const countryOptions: ScopeOption[] = [
  { value: 'brasil', label: 'Brasil' },
];

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const stateAbbreviationByName: Record<string, string> = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  goias: 'GO',
  maranhao: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};

const formatCityLabel = (city: City) => {
  const stateLabel = stateAbbreviationByName[normalizeText(city.stateName ?? '')] ?? city.stateName ?? 'Estado';
  return `${city.name} - ${stateLabel}`;
};

const toDateTimeLocalValue = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const emptyForm: RouteInput = {
  title: '',
  slug: '',
  shortDescription: '',
  coverImageUrl: '',
  cardBackgroundColor: '#6F4E37',
  featured: false,
  routeType: 'Collection',
  status: 'Draft',
  scopeType: 'City',
  scopeKey: '',
  startDate: null,
  endDate: null,
  sortOrder: null,
  shops: [],
};

const RouteFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);
  const [formData, setFormData] = useState<RouteInput>(emptyForm);
  const [searchPlace, setSearchPlace] = useState('');
  const [selectedPlaceId, setSelectedPlaceId] = useState('');

  const { data: cities = [], isLoading: citiesLoading } = useQuery({
    queryKey: ['route-cities'],
    queryFn: async () => {
      const response = await placeService.getCities();
      return response ?? [];
    },
  });

  const { data: states = [], isLoading: statesLoading } = useQuery({
    queryKey: ['route-states'],
    queryFn: async () => {
      const response = await placeService.getStates();
      return response ?? [];
    },
  });

  const { data: places = [], isLoading: placesLoading } = useQuery({
    queryKey: ['route-places'],
    queryFn: async () => {
      const response = await placeService.getAccessiblePlaces();
      return response?.Places ?? response?.places ?? [];
    },
  });

  const { data: route, isLoading: routeLoading } = useQuery({
    queryKey: ['route', id],
    queryFn: () => routesService.getRouteById(id!),
    enabled: isEdit,
  });

  const scopeOptions = useMemo<ScopeOption[]>(() => {
    switch (formData.scopeType) {
      case 'City':
        return cities.map((city: City) => ({
          value: city.id,
          label: formatCityLabel(city),
        }));
      case 'State':
        return states.map((state: State) => ({
          value: state.id,
          label: state.name,
        }));
      case 'Region':
        return regionOptions;
      case 'Country':
        return countryOptions;
      default:
        return [];
    }
  }, [cities, formData.scopeType, states]);

  useEffect(() => {
    if (route) {
      setFormData({
        title: route.title,
        slug: route.slug,
        shortDescription: route.shortDescription,
        coverImageUrl: route.coverImageUrl,
        cardBackgroundColor: route.cardBackgroundColor,
        featured: route.featured,
        routeType: route.routeType,
        status: route.status,
        scopeType: route.scopeType,
        scopeKey: route.scopeKey,
        startDate: route.startDate ?? null,
        endDate: route.endDate ?? null,
        sortOrder: route.sortOrder ?? null,
        shops: route.shops.map((shop) => ({
          coffeeShopId: shop.coffeeShopId,
          order: shop.order,
          highlightText: shop.highlightText ?? '',
          customImageUrl: shop.customImageUrl ?? '',
        })),
      });
    }
  }, [route]);

  useEffect(() => {
    if (!formData.scopeKey || scopeOptions.length === 0) {
      return;
    }

    const scopeExists = scopeOptions.some((option) => option.value === formData.scopeKey);
    if (!scopeExists) {
      setFormData((prev) => ({ ...prev, scopeKey: scopeOptions[0]?.value ?? '' }));
    }
  }, [formData.scopeKey, scopeOptions]);

  const saveMutation = useMutation({
    mutationFn: (payload: RouteInput) => (isEdit ? routesService.updateRoute(id!, payload) : routesService.createRoute(payload)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-routes'] });
      navigate('/routes');
    },
  });

  const filteredPlaces = useMemo(() => {
    const term = searchPlace.trim().toLowerCase();
    if (!term) return places;
    return places.filter((place: AdminPlaceOption) =>
      `${place.name} ${place.city} ${place.state}`.toLowerCase().includes(term)
    );
  }, [places, searchPlace]);

  const addShop = () => {
    if (!selectedPlaceId) return;
    const place = places.find((p: AdminPlaceOption) => p.id === selectedPlaceId) as AdminPlaceOption | undefined;
    if (!place) return;

    if (formData.shops.some((shop) => shop.coffeeShopId === selectedPlaceId)) {
      return;
    }

    const nextOrder = formData.shops.length + 1;
    setFormData((prev) => ({
      ...prev,
      shops: [...prev.shops, { coffeeShopId: selectedPlaceId, order: nextOrder, highlightText: '', customImageUrl: '' }],
    }));
    setSelectedPlaceId('');
  };

  const updateShop = (index: number, patch: Partial<RouteShopInput>) => {
    setFormData((prev) => ({
      ...prev,
      shops: prev.shops.map((shop, currentIndex) => (currentIndex === index ? { ...shop, ...patch } : shop)),
    }));
  };

  const removeShop = (index: number) => {
    setFormData((prev) => {
      const next = prev.shops
        .filter((_, currentIndex) => currentIndex !== index)
        .map((shop, currentIndex) => ({ ...shop, order: currentIndex + 1 }));
      return { ...prev, shops: next };
    });
  };

  const moveShop = (index: number, direction: -1 | 1) => {
    setFormData((prev) => {
      const next = [...prev.shops];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return {
        ...prev,
        shops: next.map((shop, currentIndex) => ({ ...shop, order: currentIndex + 1 })),
      };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await saveMutation.mutateAsync(formData);
  };

  if (citiesLoading || statesLoading || placesLoading || (isEdit && routeLoading)) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <Link to="/routes" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Roteiros
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">{isEdit ? 'Editar Roteiro' : 'Novo Roteiro'}</h1>
        <p className="text-gray-600">Configure os dados editoriais e a ordem manual das cafeterias.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Dados do roteiro</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Titulo *" className="px-4 py-2 border rounded-lg" required />
            <input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="Slug *" className="px-4 py-2 border rounded-lg" required />
            <input value={formData.cardBackgroundColor} onChange={(e) => setFormData({ ...formData, cardBackgroundColor: e.target.value })} placeholder="#6F4E37" className="px-4 py-2 border rounded-lg" required />
            <input type="number" value={formData.sortOrder ?? ''} onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value ? Number(e.target.value) : null })} placeholder="Ordem geral" className="px-4 py-2 border rounded-lg" />
          </div>
          <textarea value={formData.shortDescription} onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })} rows={4} placeholder="Descricao curta *" className="w-full px-4 py-2 border rounded-lg" required />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={formData.routeType}
              onChange={(e) => {
                const nextRouteType = e.target.value as RouteInput['routeType'];
                setFormData((prev) => ({
                  ...prev,
                  routeType: nextRouteType,
                  startDate: nextRouteType === 'Challenge' ? prev.startDate : null,
                  endDate: nextRouteType === 'Challenge' ? prev.endDate : null,
                }));
              }}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="Collection">Collection</option>
              <option value="Challenge">Challenge</option>
            </select>
            <select
              value={formData.scopeType}
              onChange={(e) => {
                const nextScopeType = e.target.value as RouteInput['scopeType'];
                const nextOptions = nextScopeType === 'City'
                  ? cities.map((city: City) => ({ value: city.id, label: formatCityLabel(city) }))
                  : nextScopeType === 'State'
                    ? states.map((state: State) => ({ value: state.id, label: state.name }))
                    : nextScopeType === 'Region'
                      ? regionOptions
                      : countryOptions;
                setFormData((prev) => ({
                  ...prev,
                  scopeType: nextScopeType,
                  scopeKey: nextOptions.some((option) => option.value === prev.scopeKey)
                    ? prev.scopeKey
                    : nextOptions[0]?.value ?? '',
                }));
              }}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="City">Cidade</option>
              <option value="State">Estado</option>
              <option value="Region">Regiao</option>
              <option value="Country">Pais</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cidade / Estado / Regiao / Pais</label>
            <select
              value={formData.scopeKey}
              onChange={(e) => setFormData({ ...formData, scopeKey: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg"
              required
            >
              <option value="">Selecione um escopo</option>
              {scopeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-sm text-gray-500">
            Para criar um desafio, selecione <strong>Challenge</strong> no tipo de roteiro e preencha as datas de inicio e termino.
          </p>
          {formData.routeType === 'Challenge' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex flex-col gap-2 text-sm text-gray-700">
                <span>Data de início *</span>
                <input
                  type="datetime-local"
                  value={toDateTimeLocalValue(formData.startDate)}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  className="px-4 py-2 border rounded-lg"
                  required={formData.routeType === 'Challenge'}
                />
              </label>
              <label className="flex flex-col gap-2 text-sm text-gray-700">
                <span>Data de termino *</span>
                <input
                  type="datetime-local"
                  value={toDateTimeLocalValue(formData.endDate)}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  className="px-4 py-2 border rounded-lg"
                  required={formData.routeType === 'Challenge'}
                />
              </label>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-4 items-center">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} />
              Featured
            </label>
            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as RouteInput['status'] })} className="px-4 py-2 border rounded-lg">
              <option value="Draft">Rascunho</option>
              <option value="Published">Publicado</option>
              <option value="Archived">Arquivado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Imagem de capa</label>
            <CoverImageUpload
              currentImageUrl={getCdnUrl(formData.coverImageUrl) ?? formData.coverImageUrl}
              onImageUploaded={(url) => setFormData({ ...formData, coverImageUrl: url })}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cafes</th>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              value={searchPlace}
              onChange={(e) => setSearchPlace(e.target.value)}
              placeholder="Buscar cafeterias..."
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <select value={selectedPlaceId} onChange={(e) => setSelectedPlaceId(e.target.value)} className="flex-1 px-4 py-2 border rounded-lg">
              <option value="">Selecione uma cafeteria</option>
              {filteredPlaces.map((place: AdminPlaceOption) => (
                <option key={place.id} value={place.id}>
                  {place.name} - {place.city}, {place.state}
                </option>
              ))}
            </select>
            <button type="button" onClick={addShop} className="px-4 py-2 rounded-lg bg-emerald-600 text-white inline-flex items-center">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </button>
          </div>

          <div className="space-y-3">
            {formData.shops.map((shop, index) => {
              const selectedPlace = places.find((place: AdminPlaceOption) => place.id === shop.coffeeShopId) as AdminPlaceOption | undefined;
              return (
                <div key={`${shop.coffeeShopId}-${index}`} className="border rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="font-semibold text-gray-900">{selectedPlace?.name ?? shop.coffeeShopId}</div>
                      <div className="text-sm text-gray-500">{selectedPlace ? `${selectedPlace.city}, ${selectedPlace.state}` : ''}</div>
                    </div>
                    <button type="button" onClick={() => removeShop(index)} className="text-red-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="number" value={shop.order} onChange={(e) => updateShop(index, { order: Number(e.target.value) })} className="px-3 py-2 border rounded-lg" placeholder="Ordem" />
                    <input value={shop.highlightText ?? ''} onChange={(e) => updateShop(index, { highlightText: e.target.value })} className="px-3 py-2 border rounded-lg" placeholder="Highlight opcional" />
                    <input value={shop.customImageUrl ?? ''} onChange={(e) => updateShop(index, { customImageUrl: e.target.value })} className="px-3 py-2 border rounded-lg" placeholder="Imagem customizada opcional" />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button type="button" onClick={() => moveShop(index, -1)} className="px-3 py-1 border rounded-lg text-sm">Subir</button>
                    <button type="button" onClick={() => moveShop(index, 1)} className="px-3 py-1 border rounded-lg text-sm">Descer</button>
                  </div>
                </div>
              );
            })}
            {formData.shops.length === 0 && (
              <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">Adicione cafeterias para compor o roteiro.</div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Link to="/routes" className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</Link>
          <button type="submit" disabled={saveMutation.isPending} className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saveMutation.isPending ? 'Salvando...' : 'Salvar roteiro'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RouteFormPage;
